// StaffPanel — list, add, and edit school staff (teachers + non-teaching staff)
// login_id and password are required for the staff app login. Only staff with
// designation 'Teacher' can take class attendance from the staff app — any
// teacher can cover any class, not just one they're assigned as class teacher of.
import { useEffect, useState } from 'react';
import { getStaff, createStaff, updateStaff } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const EMPTY = { name: '', phone: '', designation: 'Teacher', login_id: '', password: '' };

export default function StaffPanel() {
  const [staff,   setStaff]   = useState([]);
  const [form,    setForm]    = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [msg,     setMsg]     = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try { setStaff(await getStaff()); } catch { setMsg('Failed to load staff'); }
  }

  function startEdit(s) {
    setEditing(s.id);
    setForm({ name: s.name, phone: s.phone, designation: s.designation, login_id: s.login_id, password: '' });
  }

  function cancel() { setEditing(null); setForm(EMPTY); setMsg(''); }

  function set(key) { return e => setForm({ ...form, [key]: e.target.value }); }

  async function save(e) {
    e.preventDefault();
    try {
      const payload = { ...form, login_id: form.login_id.trim(), password: form.password.trim() };
      editing ? await updateStaff(editing, payload) : await createStaff(payload);
      setMsg(editing ? 'Staff updated ✅' : 'Staff added ✅');
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-display font-extrabold text-xl text-ink mb-1">Staff</h2>
        <p className="text-sm text-ink-soft">
          Each staff member uses their <strong>Phone Number</strong> + <strong>Password</strong> to log in to the staff app
          — Login ID is kept for your own reference, it isn't used to sign in.
          Only <strong>Teacher</strong> designation can take class attendance — any teacher can mark attendance
          for any class, so one teacher's absence never blocks a class from being marked.
        </p>
      </div>

      <Card className="p-5 mb-6 max-w-[440px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Staff' : 'Add Staff'}</h3>

        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>Name
            <input className={inputCls} required value={form.name} onChange={set('name')} />
          </label>
          <label className={labelCls}>Phone
            <input className={inputCls} required value={form.phone} onChange={set('phone')} />
          </label>
          <label className={labelCls}>Designation
            <select className={inputCls} value={form.designation} onChange={set('designation')}>
              <option value="Teacher">Teacher</option>
              <option value="Non-Teaching Staff">Non-Teaching Staff</option>
            </select>
          </label>
          <label className={labelCls}>Login ID <span className="text-critical">*</span>
            <input className={inputCls} required value={form.login_id} onChange={set('login_id')}
              placeholder="e.g. teacher.ramesh" />
          </label>
          <label className={labelCls}>
            {editing ? 'New Password (leave blank to keep current)' : 'Password'} {!editing && <span className="text-critical">*</span>}
            <input className={inputCls} type="password" value={form.password} onChange={set('password')}
              required={!editing} placeholder="Min 6 characters" />
          </label>
          <div className="flex gap-2">
            <Button type="submit">{editing ? 'Update' : 'Add Staff'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
          {msg && (
            <p className={`text-sm font-bold px-3 py-2 rounded-s inline-block w-fit ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
              {msg}
            </p>
          )}
        </form>
      </Card>

      <Card>
        <CardHeader title={`Staff (${staff.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                {['ID','Name','Phone','Designation','Login ID','Actions'].map(h => (
                  <th key={h} className="px-4 py-2.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map(s => (
                <tr key={s.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 text-ink-soft tabular-nums">{s.id}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{s.name}</td>
                  <td className="px-4 py-3">{s.phone}</td>
                  <td className="px-4 py-3">{s.designation}</td>
                  <td className="px-4 py-3"><code className="bg-paper border border-line rounded px-1.5 py-0.5 text-xs">{s.login_id}</code></td>
                  <td className="px-4 py-3">
                    <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(s)}>Edit</Button>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-ink-soft">No staff yet — add one above</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
